import { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { SiInstagram, SiLinkedin, SiWhatsapp } from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';
import { SECTIONS, CONTACT_INFO } from '@/lib/index';
import { springPresets } from '@/lib/motion';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Accueil', href: `#${SECTIONS.HERO}` },
    { label: 'À propos', href: `#${SECTIONS.INTRO}` },
    { label: 'Formations', href: `#${SECTIONS.FORMATIONS}` },
    { label: 'Méthode', href: `#${SECTIONS.METHODE}` },
    { label: 'Contact', href: `#${SECTIONS.CONTACT}` }
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    if (element) {
      const offset = headerHeight || 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <motion.header
        ref={headerRef}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={springPresets.gentle}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background/80 backdrop-blur-md border-b border-border/50 shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <motion.a
              href={`#${SECTIONS.HERO}`}
              onClick={(e) => handleNavClick(e, `#${SECTIONS.HERO}`)}
              className="text-2xl font-bold tracking-tight"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="text-foreground">Loris</span>
              <span className="text-secondary ml-1">Hug</span>
            </motion.a>

            <nav className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  whileHover={{ y: -2 }}
                  transition={springPresets.snappy}
                >
                  {item.label}
                </motion.a>
              ))}
              <motion.a
                href={`#${SECTIONS.CONTACT}`}
                onClick={(e) => handleNavClick(e, `#${SECTIONS.CONTACT}`)}
                className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={springPresets.snappy}
              >
                Commencer
              </motion.a>
            </nav>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-foreground hover:text-primary transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden bg-background/95 backdrop-blur-md border-t border-border/50"
            >
              <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="text-base font-medium text-muted-foreground hover:text-primary transition-colors py-2"
                  >
                    {item.label}
                  </a>
                ))}
                <a
                  href={`#${SECTIONS.CONTACT}`}
                  onClick={(e) => handleNavClick(e, `#${SECTIONS.CONTACT}`)}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-center hover:bg-primary/90 transition-all shadow-lg"
                >
                  Commencer
                </a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <main style={{ paddingTop: `${headerHeight}px` }}>{children}</main>

      <footer className="bg-card border-t border-border">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-foreground">Loris</span>
                <span className="text-primary ml-1">Hug</span>
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Expert en formation IA pour coiffeurs et pilotage de salon. Accompagnement concret et moderne pour les professionnels de la coiffure.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4 text-foreground">Navigation</h4>
              <nav className="flex flex-col gap-3">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4 text-foreground">Contact</h4>
              <div className="flex flex-col gap-3 mb-6">
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  {CONTACT_INFO.email}
                </a>
                {CONTACT_INFO.whatsapp && (
                  <a
                    href={`https://wa.me/${CONTACT_INFO.whatsapp.replace(/[\s\+]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    WhatsApp
                  </a>
                )}
              </div>
              <div className="flex gap-4">
                {CONTACT_INFO.instagram && (
                  <motion.a
                    href={`https://instagram.com/${CONTACT_INFO.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-muted rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    transition={springPresets.snappy}
                    aria-label="Instagram"
                  >
                    <SiInstagram size={20} />
                  </motion.a>
                )}
                {CONTACT_INFO.linkedin && (
                  <motion.a
                    href={`https://linkedin.com/in/${CONTACT_INFO.linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-muted rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    transition={springPresets.snappy}
                    aria-label="LinkedIn"
                  >
                    <SiLinkedin size={20} />
                  </motion.a>
                )}
                {CONTACT_INFO.whatsapp && (
                  <motion.a
                    href={`https://wa.me/${CONTACT_INFO.whatsapp.replace(/[\s\+]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-muted rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    transition={springPresets.snappy}
                    aria-label="WhatsApp"
                  >
                    <SiWhatsapp size={20} />
                  </motion.a>
                )}
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-muted-foreground mb-4">
              <a href="/#/mentions-legales" className="hover:text-primary transition-colors">
                Mentions légales
              </a>
              <span className="hidden sm:inline">•</span>
              <a href="/#/mentions-legales" className="hover:text-primary transition-colors">
                CGV
              </a>
              <span className="hidden sm:inline">•</span>
              <a href="/#/politique-confidentialite" className="hover:text-primary transition-colors">
                Politique de confidentialité
              </a>
              <span className="hidden sm:inline">•</span>
              <button 
                onClick={() => {
                  localStorage.removeItem('cookie-consent');
                  window.location.reload();
                }}
                className="hover:text-primary transition-colors"
              >
                Gérer les cookies
              </button>
            </div>
            <p className="text-sm text-muted-foreground">© 2026 Loris Hug. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}